import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, ChevronLeft, Video, Store } from 'lucide-react';
import { fetchMySubscriptions, cancelSubscription, type CreatorSubscription } from '@/services/creatorApi';
import { useToast } from '@/hooks/use-toast';
import { useSubscription, SUBSCRIPTION_TIERS } from '@/hooks/useSubscription';
import { fetchWallet, type Wallet } from '@/services/affiliateApi';
import { useOctOffer } from '@/services/offerApi';
import { fetchMySite, type MySite } from '@/services/siteApi';

const STATIC_ROOT = 'https://casting.heyjoe.io/api/s3/';

function resolveImageUrl(key?: string): string | null {
  if (!key) return null;
  return key.startsWith('http') ? key : `${STATIC_ROOT}${key}`;
}

const MySubscriptionsPage: React.FC = () => {
  const { toast } = useToast();
  const [subscriptions, setSubscriptions] = useState<CreatorSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [confirmCancelSub, setConfirmCancelSub] = useState<CreatorSubscription | null>(null);
  // Hey Joe Plus itself lives in user_subscriptions, not in the creator-product list
  // above, so a member saw "No active subscriptions" here. Paid Plus only: the free
  // Creator/Reader tiers report isSubscriber but canUseTools false.
  const { subscription: plus, canUseTools } = useSubscription();
  const hasPlus = !!plus && canUseTools && (plus.status === 'active' || plus.status === 'past_due');
  // Hey Joe credit, with any thank-you credit's expiry, and the October switch for monthly members.
  const [wallet, setWallet] = useState<Wallet | null>(null);
  useEffect(() => { fetchWallet().then(setWallet).catch(() => setWallet(null)); }, []);
  const octOffer = useOctOffer();
  const canSwitch = !!octOffer?.open && octOffer.kind === 'monthly';

  useEffect(() => {
    fetchMySubscriptions()
      .then(setSubscriptions)
      .catch(() => toast({ title: 'Failed to load subscriptions', variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, []);

  const handleCancel = async (sub: CreatorSubscription) => {
    setCancelingId(sub._id);
    try {
      await cancelSubscription(sub._id);
      // A free membership has no billing period: it ended just now.
      setSubscriptions(prev => prev.map(s => (s._id === sub._id
        ? (sub.free ? { ...s, status: 'canceled' as const, canceledAt: new Date().toISOString() } : { ...s, cancelAtPeriodEnd: true })
        : s)));
      toast(sub.free
        ? { title: 'Membership canceled', description: 'Your access has ended.' }
        : { title: 'Subscription will cancel', description: 'You will retain access until the end of your billing period.' });
    } catch (err: any) {
      toast({ title: 'Cancel failed', description: err.message, variant: 'destructive' });
    } finally {
      setCancelingId(null);
      setConfirmCancelSub(null);
    }
  };

  const active = subscriptions.filter(s => s.status === 'active' || s.status === 'past_due');
  const past = subscriptions.filter(s => s.status === 'canceled');

  if (loading) {
    return (
      <main
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--hj-bg)' }}
      >
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
      </main>
    );
  }

  const renderPlusRow = () => {
    if (!plus) return null;
    const known = plus.tier && (SUBSCRIPTION_TIERS as any)[plus.tier];
    const price = known && known.period ? `${known.priceDisplay}/${known.period === 'month' ? 'mo' : 'yr'}` : null;
    const plan = plus.isUnlimited ? 'Team account' : (plus.tierName || (known ? known.name : 'Member'));
    const charterEnd = plus.charterRenewal?.endsAt;
    const date = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : null);
    const pill: React.CSSProperties = { padding: '7px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 12.5, fontWeight: 500, textDecoration: 'none', fontFamily: 'inherit', minHeight: 36 };
    return (
      <div className="flex items-center gap-4" style={{ padding: 14, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
        <div className="rounded-md flex items-center justify-center flex-shrink-0" style={{ width: 72, height: 50, background: 'var(--hj-yellow)' }}>
          <span className="font-hj-display" style={{ fontSize: 18, fontWeight: 800, color: 'oklch(0.18 0.005 70)' }}>HJ+</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate m-0" style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)' }}>Hey Joe Plus</p>
            {plus.status === 'past_due' && (
              <span className="mono-label" style={{ padding: '2px 7px', borderRadius: 999, background: 'oklch(0.30 0.12 25 / 0.18)', color: 'hsl(var(--destructive))', fontWeight: 700 }}>Past due</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 4, fontSize: 12, color: 'var(--hj-ink-3)' }}>
            <span>{plan}</span>
            {price && !plus.isUnlimited && (<><span style={{ color: 'var(--hj-ink-4)' }}>·</span><span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)' }}>{price}</span></>)}
            {charterEnd ? (
              <><span style={{ color: 'var(--hj-ink-4)' }}>·</span><span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)' }}>Term ends {date(charterEnd)}</span></>
            ) : plus.cancelAtPeriodEnd && plus.currentPeriodEnd ? (
              <><span style={{ color: 'var(--hj-ink-4)' }}>·</span><span style={{ color: 'oklch(0.65 0.16 75)' }}>Cancels {date(plus.currentPeriodEnd)}</span></>
            ) : plus.currentPeriodEnd && !plus.isUnlimited ? (
              <><span style={{ color: 'var(--hj-ink-4)' }}>·</span><span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)' }}>Next bill {date(plus.currentPeriodEnd)}</span></>
            ) : null}
          </div>
        </div>
        {!plus.isUnlimited && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {canSwitch && <Link to="/offer" className="inline-flex items-center" style={{ ...pill, background: 'var(--hj-yellow)', border: 0, color: 'oklch(0.18 0.005 70)', fontWeight: 700 }}>Switch to yearly</Link>}
            <Link to="/billing" className="inline-flex items-center" style={pill}>Manage billing</Link>
          </div>
        )}
      </div>
    );
  };

  const renderRow = (sub: CreatorSubscription, pastTone = false) => (
    <div
      key={sub._id}
      className="flex items-center gap-4"
      style={{
        padding: 14,
        borderRadius: 'var(--hj-r-md)',
        background: 'var(--hj-surface-1)',
        border: '1px solid var(--hj-hairline)',
        opacity: pastTone ? 0.6 : 1,
      }}
    >
      <div
        className="rounded-md flex items-center justify-center flex-shrink-0 overflow-hidden"
        style={{ width: 72, height: 50, background: 'var(--hj-surface-2)' }}
      >
        {sub.product?.coverImage ? (
          <img
            src={resolveImageUrl(sub.product.coverImage)!}
            alt={sub.product?.title || 'Product'}
            className="w-full h-full object-cover"
          />
        ) : (
          <Video className="w-5 h-5" style={{ color: 'var(--hj-ink-4)' }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p
            className="truncate m-0"
            style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)' }}
          >
            {sub.product?.title || 'Subscription'}
          </p>
          {sub.status === 'past_due' && (
            <span
              className="mono-label"
              style={{
                padding: '2px 7px',
                borderRadius: 999,
                background: 'oklch(0.30 0.12 25 / 0.18)',
                color: 'hsl(var(--destructive))',
                fontWeight: 700,
              }}
            >
              Past due
            </span>
          )}
        </div>
        <div
          className="flex items-center gap-2"
          style={{ marginTop: 4, fontSize: 12, color: 'var(--hj-ink-3)' }}
        >
          {pastTone ? (
            <>
              <span>Canceled</span>
              {sub.currentPeriodEnd && (
                <>
                  <span style={{ color: 'var(--hj-ink-4)' }}>·</span>
                  <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)' }}>
                    Ended {new Date(sub.currentPeriodEnd).toLocaleDateString()}
                  </span>
                </>
              )}
            </>
          ) : (
            <>
              <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)' }}>
                {(sub.product?.price || 0) === 0 ? 'Free' : `${((sub.product?.price || 0) / 100).toFixed(2)}/mo`}
              </span>
              {sub.cancelAtPeriodEnd && sub.currentPeriodEnd ? (
                <>
                  <span style={{ color: 'var(--hj-ink-4)' }}>·</span>
                  <span style={{ color: 'oklch(0.65 0.16 75)' }}>
                    Cancels {new Date(sub.currentPeriodEnd).toLocaleDateString()}
                  </span>
                </>
              ) : sub.currentPeriodEnd ? (
                <>
                  <span style={{ color: 'var(--hj-ink-4)' }}>·</span>
                  <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)' }}>
                    Next bill {new Date(sub.currentPeriodEnd).toLocaleDateString()}
                  </span>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {pastTone ? (
          <span
            className="mono-label"
            style={{
              padding: '3px 9px',
              borderRadius: 999,
              border: '1px solid var(--hj-hairline)',
              color: 'var(--hj-ink-3)',
            }}
          >
            Canceled
          </span>
        ) : (
          <>
            {sub.product && (
              <Link
                to={`/purchases/${sub.productId}`}
                className="inline-flex items-center"
                style={{
                  padding: '7px 14px',
                  borderRadius: 999,
                  background: 'transparent',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink-2)',
                  fontSize: 12.5,
                  fontWeight: 500,
                  textDecoration: 'none',
                  fontFamily: 'inherit',
                  minHeight: 36,
                }}
              >
                Watch
              </Link>
            )}
            {sub.cancelAtPeriodEnd ? (
              <span
                className="mono-label"
                style={{
                  padding: '3px 9px',
                  borderRadius: 999,
                  border: '1px solid oklch(0.65 0.16 75 / 0.5)',
                  color: 'oklch(0.65 0.16 75)',
                }}
              >
                Canceling
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmCancelSub(sub)}
                disabled={cancelingId === sub._id}
                className="disabled:cursor-not-allowed"
                style={{
                  padding: '7px 12px',
                  borderRadius: 999,
                  background: 'transparent',
                  border: 0,
                  color: 'hsl(var(--destructive))',
                  fontSize: 12.5,
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  cursor: cancelingId === sub._id ? 'not-allowed' : 'pointer',
                }}
              >
                {cancelingId === sub._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Cancel'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <main
      className="mx-auto"
      style={{
        maxWidth: 800,
        padding: '0 16px 96px',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
        <Link to="/" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>My Subscriptions</span>
      </div>

      <header
        style={{
          margin: '4px 0 24px',
          padding: '22px 24px 20px',
          borderRadius: 'var(--hj-r-md)',
          background: 'var(--hj-beige)',
          border: '1px solid var(--hj-beige-line)',
          color: 'var(--hj-beige-ink)',
        }}
      >
        <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-beige-ink-2)' }}>My Subscriptions</div>
        <h1
          className="font-hj-display m-0"
          style={{
            fontSize: 'clamp(28px, 4vw, 40px)',
            lineHeight: 1,
            color: 'var(--hj-beige-ink)',
            letterSpacing: '-0.025em',
            fontWeight: 700,
          }}
        >
          Recurring access.
        </h1>
      </header>

      <div style={{ marginBottom: 20 }}><SiteHostingRow /></div>

      {subscriptions.length === 0 && !hasPlus ? (
        <div
          className="text-center"
          style={{
            padding: '60px 24px',
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1.5px dashed var(--hj-hairline)',
          }}
        >
          <Video className="w-10 h-10 mx-auto" style={{ color: 'var(--hj-ink-3)', marginBottom: 12 }} />
          <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-ink-3)' }}>
            No active subscriptions.
          </p>
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-1.5"
            style={{
              marginTop: 16,
              padding: '8px 16px',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--hj-hairline)',
              color: 'var(--hj-ink-2)',
              fontSize: 13,
              fontWeight: 500,
              textDecoration: 'none',
              fontFamily: 'inherit',
              minHeight: 40,
            }}
          >
            <Store className="w-4 h-4" /> Browse marketplace
          </Link>
        </div>
      ) : (
        <div className="flex flex-col" style={{ gap: 28 }}>
          {(active.length > 0 || hasPlus) && (
            <div>
              <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-ink-3)' }}>
                Active · {active.length + (hasPlus ? 1 : 0)}
              </div>
              <div className="flex flex-col gap-2.5">{hasPlus && renderPlusRow()}{active.map(s => renderRow(s, false))}</div>
            </div>
          )}

          {wallet && wallet.balanceCents > 0 && (
            <div style={{ padding: 14, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
              <p className="m-0" style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)' }}>
                Hey Joe credit: ${(wallet.balanceCents / 100).toFixed(2)}
              </p>
              {(wallet.bonuses || []).filter(b => b.remainingCents > 0).map((b, i) => (
                <p key={i} className="m-0" style={{ marginTop: 4, fontSize: 13, color: 'var(--hj-ink)' }}>
                  Includes ${(b.remainingCents / 100).toFixed(2)} of thank-you credit{b.expiresAt ? `, good until ${new Date(b.expiresAt).toLocaleDateString()}` : ''}. Use it on events, classes and store items; it doesn’t apply to reader bookings.
                </p>
              ))}
            </div>
          )}

          {past.length > 0 && (
            <div>
              <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-ink-3)' }}>
                Past · {past.length}
              </div>
              <div className="flex flex-col gap-2.5">{past.map(s => renderRow(s, true))}</div>
            </div>
          )}
        </div>
      )}

      {/* Cancel confirmation dialog */}
      <Dialog open={!!confirmCancelSub} onOpenChange={() => setConfirmCancelSub(null)}>
        <DialogContent
          className="max-w-sm"
          style={{
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
            color: 'var(--hj-ink)',
          }}
        >
          <DialogHeader>
            <DialogTitle asChild>
              <h2
                className="font-hj-display m-0"
                style={{
                  fontSize: 22,
                  color: 'var(--hj-ink)',
                  letterSpacing: '-0.02em',
                  fontWeight: 700,
                }}
              >
                Cancel subscription?
              </h2>
            </DialogTitle>
          </DialogHeader>
          <p style={{ fontSize: 13.5, color: 'var(--hj-ink-2)', lineHeight: 1.55, margin: 0 }}>
            {confirmCancelSub?.free ? (
              <>
                Your access to{' '}
                <strong style={{ color: 'var(--hj-ink)' }}>{confirmCancelSub?.product?.title}</strong>, and anything
                included with it, ends right away. You can join again any time.
              </>
            ) : (
              <>
            You'll keep access to{' '}
            <strong style={{ color: 'var(--hj-ink)' }}>{confirmCancelSub?.product?.title}</strong> until the end of
            your current billing period
            {confirmCancelSub?.currentPeriodEnd && (
              <>
                {' '}
                (
                <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)' }}>
                  {new Date(confirmCancelSub.currentPeriodEnd).toLocaleDateString()}
                </span>
                )
              </>
            )}
            .
              </>
            )}
          </p>
          <div className="flex justify-end gap-2" style={{ paddingTop: 8 }}>
            <button
              type="button"
              onClick={() => setConfirmCancelSub(null)}
              style={{
                padding: '8px 14px',
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--hj-hairline)',
                color: 'var(--hj-ink-2)',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 36,
              }}
            >
              Keep subscription
            </button>
            <button
              type="button"
              onClick={() => confirmCancelSub && handleCancel(confirmCancelSub)}
              disabled={cancelingId === confirmCancelSub?._id}
              className="inline-flex items-center gap-1.5 disabled:cursor-not-allowed"
              style={{
                padding: '8px 14px',
                borderRadius: 999,
                background: 'hsl(var(--destructive))',
                border: '1px solid hsl(var(--destructive))',
                color: 'hsl(var(--destructive-foreground))',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: cancelingId === confirmCancelSub?._id ? 'not-allowed' : 'pointer',
                opacity: cancelingId === confirmCancelSub?._id ? 0.6 : 1,
                minHeight: 36,
              }}
            >
              {cancelingId === confirmCancelSub?._id && (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              )}
              Cancel subscription
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
};

// Site hosting lives on /my-site; this row is how members find it from their plans.
const SiteHostingRow: React.FC = () => {
  const [data, setData] = useState<MySite | null>(null);
  useEffect(() => { fetchMySite().then(setData).catch(() => {}); }, []);
  if (!data) return null;
  const b = data.billing;
  const on = ['active', 'past_due', 'included'].includes(b.status);
  const line = b.status === 'included' ? 'Included with your account.'
    : on ? (b.mode === 'subscription' && b.plan === 'standalone' ? '$9.99/month.' : b.mode === 'charter_free' ? 'Free for your first year.' : b.mode === 'comp' ? `Free until ${b.endsAt ? new Date(b.endsAt).toLocaleDateString() : 'your Plus renewal'}.` : b.mode === 'subscription' ? `${b.interval === 'year' ? '$50/year' : '$5/month'}, billed on its own.` : `${b.interval === 'year' ? '$50/year' : '$5/month'}, on your Plus membership.`)
    : 'Host your acting website here, with a contact form that emails you and your own domain.';
  return (
    <div className="flex flex-wrap items-center justify-between" style={{ gap: 12, padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <p className="m-0" style={{ fontSize: 15, fontWeight: 600 }}>Site hosting{on && data.site ? ` · ${data.site.url.replace('https://', '')}` : ''}</p>
        <p className="m-0" style={{ marginTop: 4, fontSize: 14, color: 'var(--hj-beige-ink-2)' }}>{line}</p>
      </div>
      <Link to="/my-site" style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, padding: '0 16px', borderRadius: 999, background: 'var(--hj-ink)', color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
        {on ? 'Manage my site' : 'See site hosting'}
      </Link>
    </div>
  );
};

export default MySubscriptionsPage;
